import type { NavigateFunction } from 'react-router-dom';
import { getSiteContentById, siteContent } from './siteContent';
import {
  applySplitPanelForTopic,
  forceHomeSplitFromUrl,
  isAvatarHomeSplitContext,
  openHomeSplitForRequest,
  resolveSectionIdFromManifestTarget,
  topicTargetToPageId,
} from './avatarActions';
import { installAvatarHomeNavigationGuard } from './avatarHomeNavigationGuard';

export interface ManifestContentUpdateDetail {
  target?: string;
  content?: string;
  html?: string;
}

const PANEL_ID = 'detail-panel';
const MANIFEST_ORIGIN = 'manifestanything.ai';
const ROLEMODEL_ORIGIN = 'rolemodel.ai';

function isAvatarEmbedOrigin(origin: string): boolean {
  return origin.includes(MANIFEST_ORIGIN) || origin.includes(ROLEMODEL_ORIGIN);
}

/** Last scroll_to / set_content topic — pairs with panel-body updates */
let lastManifestScrollTarget: string | null = null;

function hideLegacyPanel(): void {
  const panel = document.querySelector<HTMLElement>(`[data-avatar-panel="${PANEL_ID}"]`);
  if (!panel) return;
  panel.hidden = true;
  panel.classList.add('avatar-panel-hidden');
}

function rememberTopicTarget(target: string): void {
  const pageId = topicTargetToPageId(target);
  if (pageId) lastManifestScrollTarget = pageId;
}

function resolveSameOriginPath(url: string): string | null {
  try {
    if (url.startsWith('/')) return url;
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function getToolTarget(args: Record<string, unknown>): string | undefined {
  const target = args.target ?? args.id ?? args.sectionId ?? args.section;
  return typeof target === 'string' ? target : undefined;
}

function getNavigateRaw(args: Record<string, unknown>): string | undefined {
  const raw = args.url ?? args.path ?? args.href ?? args.route ?? args.target ?? args.id;
  return typeof raw === 'string' ? raw : undefined;
}

function getMessageRequestId(data: Record<string, unknown>): string | null {
  const id = data.requestId;
  return typeof id === 'string' ? id : null;
}

function replyManifestToolResult(
  event: MessageEvent,
  tool: string,
  args: Record<string, unknown>,
  result: Record<string, unknown>
): void {
  const data = event.data;
  if (!data || typeof data !== 'object') return;
  const source = event.source;
  if (!source || typeof (source as Window).postMessage !== 'function') return;

  try {
    (source as Window).postMessage(
      {
        type: 'manifest-avatar-tool-result',
        tool,
        args,
        result,
        requestId: getMessageRequestId(data as Record<string, unknown>),
      },
      event.origin
    );
  } catch {
    /* cross-origin postMessage may fail */
  }
}

/** Update visible split panel from set_content / show_panel targets. */
function applySplitFromContentTool(tool: string, args: Record<string, unknown>): boolean {
  const name = (tool || '').toLowerCase();
  const target = getToolTarget(args);

  if (name === 'show_panel') {
    if (lastManifestScrollTarget) {
      hideLegacyPanel();
      return applySplitPanelForTopic(lastManifestScrollTarget);
    }
    return false;
  }

  if (name === 'set_content' && target) {
    if (target === 'panel-body' && lastManifestScrollTarget) {
      hideLegacyPanel();
      return applySplitPanelForTopic(lastManifestScrollTarget);
    }
    rememberTopicTarget(target);
    hideLegacyPanel();
    return applySplitPanelForTopic(target);
  }

  return false;
}

function applySplitFromScrollTarget(target: string): boolean {
  rememberTopicTarget(target);
  hideLegacyPanel();
  return applySplitPanelForTopic(target);
}

function interceptManifestToolOnHome(
  event: MessageEvent,
  tool: string,
  args: Record<string, unknown>
): boolean {
  if (!isAvatarHomeSplitContext()) return false;

  const name = (tool || '').toLowerCase();

  if (name === 'scroll_to' || name === 'highlight') {
    const target = getToolTarget(args);
    if (!target) return false;
    const opened = applySplitFromScrollTarget(target);
    event.stopImmediatePropagation();
    replyManifestToolResult(
      event,
      tool,
      args,
      opened
        ? { ok: true, target, intercepted: true }
        : { ok: false, error: 'Could not open in split panel', target }
    );
    return true;
  }

  if (name === 'navigate' || name === 'open_url') {
    const raw = getNavigateRaw(args);
    if (!raw) return false;
    const opened = forceHomeSplitFromUrl(raw);
    event.stopImmediatePropagation();
    if (opened) {
      hideLegacyPanel();
      replyManifestToolResult(event, tool, args, {
        ok: true,
        url: raw,
        intercepted: true,
        opened: true,
      });
    } else {
      replyManifestToolResult(event, tool, args, {
        ok: false,
        error: 'Could not open in split panel',
        url: raw,
      });
    }
    return true;
  }

  if (name === 'set_content' || name === 'show_panel') {
    const opened = applySplitFromContentTool(tool, args);
    event.stopImmediatePropagation();
    replyManifestToolResult(
      event,
      tool,
      args,
      opened
        ? { ok: true, intercepted: true, target: getToolTarget(args) ?? lastManifestScrollTarget }
        : {
            ok: false,
            error: 'Could not open in split panel',
            target: getToolTarget(args) ?? lastManifestScrollTarget,
          }
    );
    return true;
  }

  return false;
}

function syncSplitFromManifestTool(tool: string, args: Record<string, unknown>): void {
  if (!isAvatarHomeSplitContext()) return;

  const name = (tool || '').toLowerCase();
  const target = getToolTarget(args);

  if ((name === 'scroll_to' || name === 'highlight') && target) {
    applySplitFromScrollTarget(target);
    return;
  }

  if (name === 'set_content' || name === 'show_panel') {
    applySplitFromContentTool(tool, args);
    return;
  }

  if (name === 'navigate' || name === 'open_url') {
    const raw = getNavigateRaw(args);
    if (raw) forceHomeSplitFromUrl(raw);
  }
}

const SPLIT_SYNC_WRAPPED = '__manifestSplitSyncWrapped';

/** Sync split panel when host-tools runs tools (chips, devtools, direct calls). */
export function installManifestHomeSplitSync(): void {
  const original = window.__manifestRunAvatarTool;
  if (!original || (original as Record<string, unknown>)[SPLIT_SYNC_WRAPPED]) return;

  const wrapped = (tool: string, args?: Record<string, unknown>) => {
    const name = (tool || '').toLowerCase();
    const payload = args && typeof args === 'object' ? args : {};

    if (isAvatarHomeSplitContext()) {
      if (name === 'navigate' || name === 'open_url') {
        const raw = getNavigateRaw(payload);
        if (typeof raw === 'string') {
          const opened = forceHomeSplitFromUrl(raw);
          if (opened) {
            hideLegacyPanel();
            return { ok: true, url: raw, intercepted: true, opened: true };
          }
          return { ok: false, error: 'Could not open in split panel', url: raw };
        }
      }

      if (name === 'scroll_to' || name === 'highlight') {
        const target = getToolTarget(payload);
        if (target) {
          const opened = applySplitFromScrollTarget(target);
          if (opened) return { ok: true, target, intercepted: true };
          return { ok: false, error: 'Could not open in split panel', target };
        }
      }

      if (name === 'set_content' || name === 'show_panel') {
        const opened = applySplitFromContentTool(tool, payload);
        if (opened) return { ok: true, intercepted: true };
        return { ok: false, error: 'Could not open in split panel' };
      }
    }

    syncSplitFromManifestTool(tool, payload);
    return original(tool, payload);
  };

  (wrapped as Record<string, unknown>)[SPLIT_SYNC_WRAPPED] = true;
  window.__manifestRunAvatarTool = wrapped;
}

/** Open Manifest topic in the right split panel (chips / tests). */
export function openManifestTopicPanel(sectionId: string): void {
  lastManifestScrollTarget = topicTargetToPageId(sectionId) ?? sectionId;
  applySplitPanelForTopic(sectionId);
}

function handleNavigateIntent(
  raw: string,
  event?: MessageEvent,
  navigate?: NavigateFunction
): void {
  if (isAvatarHomeSplitContext()) {
    if (forceHomeSplitFromUrl(raw)) {
      hideLegacyPanel();
      event?.stopImmediatePropagation();
      return;
    }
    const sectionId = resolveSectionIdFromManifestTarget(raw);
    if (sectionId && applySplitPanelForTopic(sectionId)) {
      event?.stopImmediatePropagation();
    }
    return;
  }

  const path = resolveSameOriginPath(raw);
  if (path && navigate) {
    event?.stopImmediatePropagation();
    navigate(path);
  }
}

function handleContentUpdated(event: Event): void {
  if (!isAvatarHomeSplitContext()) return;

  const detail = (event as CustomEvent<ManifestContentUpdateDetail>).detail || {};
  const target = detail.target;
  if (!target) return;

  if (target === 'panel-body' && lastManifestScrollTarget) {
    applySplitPanelForTopic(lastManifestScrollTarget);
    hideLegacyPanel();
    return;
  }

  const pageId = topicTargetToPageId(target);
  if (pageId) {
    lastManifestScrollTarget = pageId;
    applySplitPanelForTopic(target);
    hideLegacyPanel();
  }
}

function getToolArgs(data: Record<string, unknown>): Record<string, unknown> {
  const args = data.args;
  if (args && typeof args === 'object') return args as Record<string, unknown>;
  const result = data.result;
  if (result && typeof result === 'object') {
    const nested = (result as Record<string, unknown>).args;
    if (nested && typeof nested === 'object') return nested as Record<string, unknown>;
  }
  return {};
}

function getToolName(data: Record<string, unknown>): string | undefined {
  const tool = data.tool ?? data.action;
  return typeof tool === 'string' ? tool : undefined;
}

function handleToolResult(event: MessageEvent): void {
  if (!isAvatarEmbedOrigin(event.origin)) return;
  const data = event.data;
  if (!data || typeof data !== 'object' || data.type !== 'manifest-avatar-tool-result') return;

  const tool = getToolName(data as Record<string, unknown>);
  const args = getToolArgs(data as Record<string, unknown>);
  if (!tool) return;

  if (tool === 'scroll_to' || tool === 'highlight') {
    const target = getToolTarget(args);
    if (target) applySplitFromScrollTarget(target);
    return;
  }

  if (tool === 'set_content' || tool === 'show_panel') {
    applySplitFromContentTool(tool, args);
    return;
  }

  if (tool === 'navigate' || tool === 'open_url') {
    const raw = getNavigateRaw(args);
    if (raw) handleNavigateIntent(raw, event);
  }
}

function handleManifestToolMessage(event: MessageEvent, navigate?: NavigateFunction): void {
  if (!isAvatarEmbedOrigin(event.origin)) return;
  const data = event.data;
  if (!data || typeof data !== 'object' || data.type !== 'manifest-avatar-tool') return;

  const tool = getToolName(data as Record<string, unknown>);
  const args = getToolArgs(data as Record<string, unknown>);
  if (!tool) return;

  if (interceptManifestToolOnHome(event, tool, args)) return;

  syncSplitFromManifestTool(tool, args);

  if (tool === 'navigate' || tool === 'open_url') {
    const raw = getNavigateRaw(args);
    if (raw) handleNavigateIntent(raw, event, navigate);
  }
}

function handleLooseManifestMessage(event: MessageEvent): void {
  if (!isAvatarHomeSplitContext()) return;
  if (!isAvatarEmbedOrigin(event.origin)) return;

  const data = event.data;
  if (!data || typeof data !== 'object') return;

  const record = data as Record<string, unknown>;
  const type = typeof record.type === 'string' ? record.type : '';
  if (type === 'manifest-avatar-tool' || type === 'manifest-avatar-tool-result') return;

  // Concierge / Role Model embed page actions + session lifecycle
  if (record.source === 'rolemodel-embed') {
    if (record.type === 'session-state') return;

    const action = typeof record.action === 'string' ? record.action : '';
    const target = typeof record.target === 'string' ? record.target : '';
    if (!target) return;
    if (action === 'scroll_to' || action === 'highlight' || action === 'click') {
      applySplitFromScrollTarget(target);
    }
    return;
  }

  const tool = getToolName(record);
  const args = getToolArgs(record);
  const target = getToolTarget(args);

  if (tool === 'scroll_to' || tool === 'highlight') {
    if (target) applySplitFromScrollTarget(target);
    return;
  }

  const raw = getNavigateRaw(args) ?? target;
  if (raw) forceHomeSplitFromUrl(raw);
}

export function setupManifestAvatarBridge(navigate?: NavigateFunction): () => void {
  installManifestHomeSplitSync();
  const teardownNavGuard = installAvatarHomeNavigationGuard();

  const onContentUpdated = handleContentUpdated;
  const onToolResult = (e: MessageEvent) => handleToolResult(e);
  const onManifestTool = (e: MessageEvent) => handleManifestToolMessage(e, navigate);
  const onLooseManifest = (e: MessageEvent) => handleLooseManifestMessage(e);

  window.addEventListener('manifest-avatar-content-updated', onContentUpdated);
  window.addEventListener('message', onToolResult);
  window.addEventListener('message', onManifestTool, true);
  window.addEventListener('message', onLooseManifest, true);

  return () => {
    teardownNavGuard();
    window.removeEventListener('manifest-avatar-content-updated', onContentUpdated);
    window.removeEventListener('message', onToolResult);
    window.removeEventListener('message', onManifestTool, true);
    window.removeEventListener('message', onLooseManifest, true);
  };
}

export function closeManifestDetailPanel(): void {
  hideLegacyPanel();
}

export function prefillContentSlot(target: string, text: string): void {
  if (target === 'panel-body') return;
  const slot = document.querySelector(`[data-avatar-content="${target}"]`);
  if (slot && !slot.textContent?.trim()) {
    slot.textContent = text;
  }
}

export function getSiteMapTopicsFromRegistry(): Array<{
  id: string;
  contentTarget: string;
  summary: string;
}> {
  return siteContent.map((item) => ({
    id: item.id,
    contentTarget: `${item.id}-detail`,
    summary: item.shortDescription,
  }));
}

export function removeManifestBubbleWidget(): void {
  document.getElementById('manifest-avatar-launcher')?.remove();
  document
    .querySelectorAll(
      [
        '[data-manifest-avatar]:not(#manifest-avatar-container iframe)',
        '[class*="manifest-bubble"]',
        '[id*="manifest-bubble"]',
        'button[aria-label*="Need help"]',
        'button[aria-label*="Open Milan"]',
        'button[aria-label*="Open Assistant"]',
        '.sl-label',
      ].join(', ')
    )
    .forEach((el) => {
      if (el.tagName === 'IFRAME' && el.closest('#manifest-avatar-container')) return;
      const btn = el.closest('button');
      if (btn?.getAttribute('aria-label')?.includes('Open')) {
        btn.remove();
        return;
      }
      if (el.tagName !== 'IFRAME' || !el.closest('#manifest-avatar-container')) {
        el.remove();
      }
    });

  document.querySelectorAll('button').forEach((btn) => {
    const label = btn.getAttribute('aria-label') || '';
    const text = btn.textContent || '';
    if (
      label.startsWith('Open ') &&
      (text.includes('Need help') || text.includes('💬'))
    ) {
      btn.nextElementSibling?.remove();
      btn.remove();
    }
  });
}
